import React, { useState } from "react";
import clsx from "clsx";

// Font Awesome
import { faCircleChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Formik
import { Form, Formik } from "formik";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import ButtonBase from "@material-ui/core/ButtonBase";

// Config
import colors from "src/config/colors";
import useTranslate from "src/hooks/useTranslate";

// Context
import { useNewCaseFlow } from "src/context/NewCaseFlowProvider";

// Components
import Button from "../../components/Button";
import CustomField from "../../components/CustomField";
import ReferenceNumberDialog from "../../components/ReferenceNumberDialog";
import Text from "../../components/Text/Text";

const useStyles = makeStyles(theme => ({
  boxContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    gap: 16
  },
  textWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    marginBottom: "48px",
    padding: 50
  },
  title: {
    fontSize: "40px",
    color: colors.riverBed
  },
  buttonWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 12
  },
  form: {
    position: "relative"
  },
  refInput: {
    backgroundColor: "#fff",
    marginBottom: 0
  },
  buttonBase: {
    position: "absolute",
    top: 0,
    right: 0,
    height: "48px",
    width: "48px"
  },
  icon: {
    color: colors.pictonBlue,
    width: "24px",
    height: "24px"
  },
  iconDisabled: {
    color: "#E0E0E0"
  },
  or: {
    margin: "0 auto",
    textTransform: "capitalize"
  },
  button: {
    width: "700px",
    borderRadius: "12px",
    textTransform: "uppercase",
    fontSize: "16px"
  }
}));

const CasesPageCreateCase = () => {
  const classes = useStyles();
  const [openRefDialog, setOpenRefDialog] = useState(false);
  const [initialValue, setInitialValue] = useState("");
  const getText = useTranslate();
  const { open: openNewCaseFlow } = useNewCaseFlow();

  const handleOpenRefDialog = ({ refNumber }, { setSubmitting }) => {
    setSubmitting(false);
    if (refNumber) {
      setInitialValue(refNumber);
      setOpenRefDialog(true);
    }
  };

  const handleCloseRefDialog = () => {
    setOpenRefDialog(false);
  };

  const onClickNewIncident = () => {
    openNewCaseFlow();
  };

  return (
    <>
      {openRefDialog && (
        <ReferenceNumberDialog
          onClose={handleCloseRefDialog}
          initialValue={initialValue}
        />
      )}
      <Box className={classes.boxContainer}>
        <Box className={classes.textWrapper}>
          <Text
            className={classes.title}
            text="casesPage.emptyText"
            type="heading1"
          />
          <Text
            color="textSecondary"
            text="createCasePage.newCaseFlow.uploadAndShareIt"
          />
        </Box>
        <Box className={classes.buttonWrapper}>
          <Text
            type="contentLabel3"
            text="createCasePage.newCaseFlow.startWithReferenceNumber"
          />
          <Formik
            initialValues={{
              refNumber: ""
            }}
            onSubmit={handleOpenRefDialog}
          >
            {({ values }) => (
              <Form className={classes.form}>
                <CustomField
                  autoComplete="off"
                  name="refNumber"
                  placeholder={getText(
                    "createCasePage.givenReferenceNumber.referenceNumberLabel"
                  )}
                  className={classes.refInput}
                />
                <ButtonBase
                  className={classes.buttonBase}
                  disabled={!values.refNumber}
                  type="submit"
                >
                  <FontAwesomeIcon
                    className={clsx(classes.icon, {
                      [classes.iconDisabled]: !values.refNumber
                    })}
                    icon={faCircleChevronRight}
                  />
                </ButtonBase>
              </Form>
            )}
          </Formik>
          <Text
            type="contentLabel2"
            color="textSecondary"
            className={classes.or}
            text="common.prompts.or"
          />
          <Button
            variant="contained"
            disableElevation
            color="primary"
            fullWidth
            className={classes.button}
            onClick={onClickNewIncident}
          >
            {getText("casesPage.createButton")}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default CasesPageCreateCase;
